import React, { useEffect, useState } from 'react';
import { limitFill, pad } from '@cloudinary/url-gen/actions/resize';
import { quality } from '@cloudinary/url-gen/actions/delivery';
import styled from 'styled-components';
import { nafColor } from '@nafcore/theme';
import SchemaVideo from '../structured-data/SchemaVideo';
import { createNorwegianDateString } from '../../utils/createNorwegianDateString';
import { useCloudinaryWithVideoPlayer } from '../../hooks/Cloudinary/useCloudinaryWithVideoPlayer';
import { useCloudinary } from '../../hooks/Cloudinary/useCloudinary';
import useSelector from '../../redux/typedHooks';
import { VideoPlayerProps } from './DynamicVideoPlayer';
import { GTMEnrichedWindow } from '../../../../types/GTM/GTMEnrichedWindow';

export const VideoPlayer = ({
  video: videoProp,
  onVideoStart,
  onVideoPlay,
  videoPlayerOptions,
  image,
}: VideoPlayerProps) => {
  const { source_caption, publicId, caption, duration, source_altText, altText, key } = videoProp;
  const { imageCloudName } = useSelector((state) => state.application);

  const [video, setVideo] = useState<string | undefined>();
  const [videoPlayer, setVideoPlayer] = useState<any>();

  const cld = useCloudinary();
  const cloudinaryWithVideoPlayer = useCloudinaryWithVideoPlayer();

  useEffect(() => {
    const initVideoPlayer = async () => {
      if (publicId && !videoPlayer) {
        if (cloudinaryWithVideoPlayer && typeof document !== 'undefined') {
          if (key) {
            const player = cloudinaryWithVideoPlayer.videoPlayer(`#video-player-${key}`, {
              cloud_name: imageCloudName,
              controls: true,
              fluid: true,
              autoplay: false,
              preload: 'auto',
              muted: false,
              showLogo: false,
              playsInline: true,
              colors: {
                base: nafColor.signature.black,
                text: nafColor.signature.white,
                accent: nafColor.signature.yellow,
              },
              posterOptions: { publicId: image?.publicId },
              ...videoPlayerOptions,
            });
            player.source(publicId, { sourceTypes: ['hls'], transformation: { streaming_profile: 'full_hd' } });
            player.on('play', onVideoPlay);
            player.on('start', onVideoStart);
            setVideoPlayer(player);
          }
        }
      }
    };

    initVideoPlayer().then();
  }, [
    publicId,
    cloudinaryWithVideoPlayer,
    key,
    videoPlayer,
    onVideoStart,
    onVideoPlay,
    imageCloudName,
    videoPlayerOptions,
    image?.publicId,
  ]);

  const isGTMEnriched =
    typeof (window as unknown as GTMEnrichedWindow) !== 'undefined' &&
    typeof (window as unknown as GTMEnrichedWindow)?.ga;

  useEffect(() => {
    if (videoPlayer?.options?.playerOptions) {
      videoPlayer.options.playerOptions.analytics = {
        events: ['play', 'pause', 'percentsplayed', 'start', 'ended', 'fullscreenchange'],
      };
    }
  }, [videoPlayer?.options?.playerOptions, isGTMEnriched]);

  const videoObject = (publicId && cld?.video(publicId)) || undefined;

  useEffect(() => {
    if (!video && videoObject) {
      setVideo(videoObject?.resize(pad().width(500)).delivery(quality('auto')).toURL());
    }
  }, [videoObject, video]);

  const poster = publicId && cld?.video(`${publicId}.jpg`).resize(limitFill().width(795)).toURL();

  const videoCaption = caption || source_caption;
  // TODO: update this date when it is available from cloudinary/sanity
  const videoUploadDate = new Date();
  return (
    <div>
      <SchemaVideo
        uploadDate={createNorwegianDateString(videoUploadDate)}
        name={videoCaption || 'NAF - video'}
        description={altText || source_altText || ''}
        posterUrl={poster}
        contentUrl={video}
        duration={`${duration}`}
      />
      <VideoElement>
        {/* TODO: add captions */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video id={`video-player-${key}`} poster={poster} />
      </VideoElement>
    </div>
  );
};

const VideoElement = styled.div`
  position: relative;
  cursor: pointer;

  video {
    width: 100%;
    outline: none;
  }
`;
